/**
 * TopicCard Component
 * Individual topic score card with mini progress ring
 */
import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { DesktopTheme, getScoreColor, getPillarColor } from '../theme/desktopTheme';

const MiniProgressRing = ({ progress, size = 48, strokeWidth = 4, color }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <Svg width={size} height={size}>
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#e2e8f0"
        strokeWidth={strokeWidth}
        fill="none"
      />
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </Svg>
  );
};

const TopicCard = ({
  topic,
  score,
  previousScore,
  pillar,
  date,
  onPress,
  onLongPress,
  isNotFilled = false,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const scoreColor = isNotFilled ? colors.textMuted : getScoreColor(score || 0);
  const pillarColor = getPillarColor(pillar);

  // Calculate trend
  const trend =
    previousScore !== undefined && previousScore !== null && !isNotFilled
      ? (score || 0) - previousScore
      : null;

  const getTrendIcon = () => {
    if (trend === null || trend === 0) return '';
    return trend > 0 ? '↑' : '↓';
  };

  const getTrendColor = () => {
    if (trend === null || trend === 0) return colors.textMuted;
    return trend > 0 ? colors.success : colors.error;
  };

  return (
    <TouchableOpacity
      style={[
        styles.card,
        isHovered && styles.cardHovered,
        isNotFilled && styles.cardNotFilled,
        style,
      ]}
      onPress={onPress}
      onLongPress={onLongPress}
      onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      activeOpacity={0.8}
      delayLongPress={500}
    >
      {/* Progress Ring */}
      <View style={styles.progressContainer}>
        {isNotFilled ? (
          <View style={styles.notFilledRing}>
            <Text style={styles.notFilledIcon}>○</Text>
          </View>
        ) : (
          <>
            <MiniProgressRing
              progress={score || 0}
              size={56}
              strokeWidth={5}
              color={scoreColor}
            />
            <View style={styles.scoreOverlay}>
              <Text style={[styles.scoreValue, { color: scoreColor }]}>
                {Math.round(score || 0)}
              </Text>
            </View>
          </>
        )}
      </View>

      {/* Topic Info */}
      <View style={styles.infoContainer}>
        <Text style={styles.topicName} numberOfLines={2}>
          {topic}
        </Text>

        {isNotFilled ? (
          <Text style={styles.notFilledText}>Not evaluated</Text>
        ) : (
          <View style={styles.metaRow}>
            {trend !== null && (
              <View style={styles.trendBadge}>
                <Text style={[styles.trendText, { color: getTrendColor() }]}>
                  {getTrendIcon()} {Math.abs(Math.round(trend))}%
                </Text>
              </View>
            )}
            {date && date !== 'N/A' && (
              <Text style={styles.dateText}>{date}</Text>
            )}
          </View>
        )}
      </View>

      {/* Pillar indicator */}
      <View style={[styles.pillarIndicator, { backgroundColor: pillarColor }]} />
    </TouchableOpacity>
  );
};

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.sm,
    ...shadows.sm,
    borderWidth: 1,
    borderColor: colors.border,
    position: 'relative',
    overflow: 'hidden',
  },
  cardHovered: {
    ...shadows.md,
    borderColor: colors.primary,
    backgroundColor: '#fafafa',
  },
  cardNotFilled: {
    opacity: 0.7,
    borderStyle: 'dashed',
  },

  // Progress
  progressContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  scoreOverlay: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreValue: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
  },
  notFilledRing: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 5,
    borderColor: colors.border,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  notFilledIcon: {
    fontSize: 20,
    color: colors.textMuted,
  },

  // Info
  infoContainer: {
    flex: 1,
  },
  topicName: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  trendBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.contentBg,
    marginRight: spacing.sm,
  },
  trendText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.tiny,
  },
  dateText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.tiny,
    color: colors.textMuted,
  },
  notFilledText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
    fontStyle: 'italic',
  },

  // Pillar indicator
  pillarIndicator: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderTopLeftRadius: borderRadius.md,
    borderBottomLeftRadius: borderRadius.md,
  },
});

export default TopicCard;
